import { buildInfo, shortCommit } from '@brush/build-info';
import { AboutDialog, VersionBadge, apiVersionState } from '@brush/ui';
import { useState } from 'react';
import { useApiVersion } from '../queries';

/**
 * The version line in the room rail, and the detail behind it.
 *
 * In the header rather than at the foot of anything: this shell is exactly one viewport tall and
 * the document never scrolls, so there is no page footer to put it in — and the rundown rail
 * beside the content is its own scrolling region, which would carry the line off screen under a
 * long running order.
 *
 * Not routed through `useUnsaved().leave` like the section nav and the room switcher: opening a
 * dialog over the current screen leaves the draft underneath it exactly where it was.
 *
 * Rendered twice, and the two are not alternatives to each other. The default is the header's,
 * shown from `md` — below that the nav is already scrolling horizontally and there is no width to
 * spend on a line nobody reads at a bus stop. `HeaderMenu` renders the other with a `className` of
 * its own, because on a phone that panel *is* the chrome and the build somebody is being asked to
 * read out over the phone has nowhere else to be. Both are mounted at once while that panel is
 * open — the header's copy is hidden by CSS rather than unmounted — so they hold two independent
 * dialog states. Only one is ever *reachable* at a given width, which is what keeps that harmless.
 */
export function About({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const version = useApiVersion(open);

  return (
    <>
      <VersionBadge
        version={buildInfo.version}
        commit={shortCommit(buildInfo.commit)}
        onClick={() => setOpen(true)}
        className={className ?? 'hidden text-rail-300 hover:text-white md:inline'}
      />
      {open ? (
        <AboutDialog
          appLabel="Room console"
          client={buildInfo}
          api={apiVersionState(version)}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}
